
export const associationPaths = {
  '/associations': {
    get: {
      summary: 'List associations',
      operationId: 'listAssociations',
      tags: ['Associations'],
      parameters: [
        {
          name: 'search',
          in: 'query',
          schema: {
            type: 'string',
          },
          description: 'Search term to filter associations by name',
        },
        {
          name: 'type',
          in: 'query',
          schema: {
            type: 'string',
          },
          description: 'Filter associations by type (Condominium, HOA, COOP)',
        },
        {
          name: 'limit',
          in: 'query',
          schema: {
            type: 'integer',
          },
          description: 'Maximum number of associations to return',
        },
        {
          name: 'offset',
          in: 'query',
          schema: {
            type: 'integer',
          },
          description: 'Number of associations to skip',
        },
      ],
      responses: {
        '200': {
          description: 'A list of associations',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: {
                  $ref: '#/components/schemas/Association',
                },
              },
            },
          },
        },
      },
    },
    post: {
      summary: 'Create a new association',
      operationId: 'createAssociation',
      tags: ['Associations'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/NewAssociation',
            },
          },
        },
      },
      responses: {
        '201': {
          description: 'The created association',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Association',
              },
            },
          },
        },
      },
    },
  },
  '/associations/{id}': {
    get: {
      summary: 'Get a specific association by ID',
      operationId: 'getAssociation',
      tags: ['Associations'],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'Association ID',
        },
      ],
      responses: {
        '200': {
          description: 'Association details',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Association',
              },
            },
          },
        },
        '404': {
          description: 'Association not found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse',
              },
            },
          },
        },
      },
    },
    patch: {
      summary: 'Update an association',
      operationId: 'updateAssociation',
      tags: ['Associations'],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'Association ID',
        },
      ],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/UpdateAssociation',
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Updated association',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Association',
              },
            },
          },
        },
        '404': {
          description: 'Association not found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse',
              },
            },
          },
        },
      },
    },
    delete: {
      summary: 'Delete an association',
      operationId: 'deleteAssociation',
      tags: ['Associations'],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'Association ID',
        },
      ],
      responses: {
        '200': {
          description: 'Association deleted',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: {
                    type: 'boolean',
                  },
                },
              },
            },
          },
        },
        '404': {
          description: 'Association not found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse',
              },
            },
          },
        },
      },
    },
  },
  '/associations/{id}/document-groups': {
    get: {
      summary: 'Get document groups for an association',
      operationId: 'getAssociationDocumentGroups',
      tags: ['Associations'],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'Association ID',
        },
      ],
      responses: {
        '200': {
          description: 'Document groups for the association',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: {
                  $ref: '#/components/schemas/DocumentGroup',
                },
              },
            },
          },
        },
        '404': {
          description: 'Association not found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse',
              },
            },
          },
        },
      },
    },
    put: {
      summary: 'Update document groups for an association',
      operationId: 'updateAssociationDocumentGroups',
      tags: ['Associations'],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'Association ID',
        },
      ],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                document_groups: {
                  type: 'array',
                  items: {
                    type: 'integer',
                  },
                },
              },
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Updated association with document groups',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Association',
              },
            },
          },
        },
        '404': {
          description: 'Association not found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse',
              },
            },
          },
        },
      },
    },
  },
  '/associations/import': {
    post: {
      summary: 'Bulk import associations from CSV',
      operationId: 'importAssociations',
      tags: ['Associations'],
      requestBody: {
        required: true,
        content: {
          'multipart/form-data': {
            schema: {
              type: 'object',
              properties: {
                file: {
                  type: 'string',
                  format: 'binary',
                  description: 'CSV file containing association data. Values containing commas should be enclosed in double quotes (e.g., "Company, Inc.").'
                },
                management_company_id: {
                  type: 'integer',
                  description: 'ID of the management company these associations belong to'
                }
              },
              required: ['file', 'management_company_id']
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Import result',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: {
                    type: 'boolean',
                    description: 'Whether the import was successful'
                  },
                  message: {
                    type: 'string',
                    description: 'Description of the import result'
                  },
                  imported: {
                    type: 'integer',
                    description: 'Number of successfully imported associations'
                  },
                  total: {
                    type: 'integer',
                    description: 'Total number of associations in the CSV'
                  },
                  errors: {
                    type: 'array',
                    items: {
                      type: 'string'
                    },
                    description: 'List of errors encountered during import'
                  }
                }
              }
            }
          }
        },
        '400': {
          description: 'Bad request, invalid file format or missing data',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse'
              }
            }
          }
        }
      }
    }
  },
  '/associations/{id}/documents': {
    get: {
      summary: 'Get documents for an association',
      operationId: 'getAssociationDocuments',
      tags: ['Associations'],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'Association ID',
        },
      ],
      responses: {
        '200': {
          description: 'Documents for the association',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: {
                  $ref: '#/components/schemas/Document',
                },
              },
            },
          },
        },
        '404': {
          description: 'Association not found',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse',
              },
            },
          },
        },
      },
    },
  },
  '/associations/export-csv': {
    post: {
      summary: 'Export associations to CSV',
      operationId: 'exportAssociations',
      tags: ['Associations'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                management_company_id: {
                  type: 'integer',
                  description: 'ID of the management company to export associations from'
                }
              },
              required: ['management_company_id']
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'CSV file containing association data',
          content: {
            'text/csv': {
              schema: {
                type: 'string',
                format: 'binary',
                description: 'CSV file containing association data'
              }
            }
          }
        },
        '400': {
          description: 'Bad request, missing management company ID',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/ErrorResponse'
              }
            }
          }
        }
      }
    }
  }
};
